import { useState, useEffect, useCallback, useRef } from 'react'
import { api, useAuth } from '../context/AuthContext'
import { useSocket } from '../context/SocketContext'
import { API_FILE_URL } from '../constants/api'

// ── Helpers ───────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || API_FILE_URL
const rwf      = (n) => `${Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} RWF`
const fmt      = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtTime  = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

const avatarBg = (str = '') => {
  const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return c[Math.abs(h) % c.length]
}
const initials = (n = '') => (n.trim().charAt(0) || '?').toUpperCase()

const parseImage = (images) => {
  try {
    const arr = typeof images === 'string' ? JSON.parse(images) : images
    if (!Array.isArray(arr) || arr.length === 0) return null
    const raw = arr[0]
    if (typeof raw === 'string' && raw.startsWith('http')) return raw
    const filename = raw.replace(/\\/g, '/').split('/').pop()
    return `${API_BASE}/${filename}`
  } catch { return null }
}

// ── Status config ─────────────────────────────────────────────────────────────
const ORDER_STATUS = {
  pending:    { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', label: 'Pending'    },
  confirmed:  { bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6', label: 'Confirmed'  },
  processing: { bg: '#f5f3ff', color: '#7c3aed', dot: '#8b5cf6', label: 'Processing' },
  shipped:    { bg: '#f0f9ff', color: '#0369a1', dot: '#0ea5e9', label: 'Shipped'    },
  delivered:  { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Delivered'  },
  cancelled:  { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Cancelled'  },
  returned:   { bg: '#fff7ed', color: '#c2410c', dot: '#f97316', label: 'Returned'   },
}
const os = (s) => ORDER_STATUS[s?.toLowerCase()] ?? { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: s || '—' }

const TIMELINE = ['pending','confirmed','processing','shipped','delivered']

const NEXT_STATUS = {
  pending:    ['confirmed',  'cancelled'],
  confirmed:  ['processing', 'cancelled'],
  processing: ['shipped',    'cancelled'],
  shipped:    ['delivered'],
}

const FILTERS = [
  { value: '',            label: 'All'           },
  { value: 'pending',     label: 'Pending'       },
  { value: 'confirmed',   label: 'Confirmed'     },
  { value: 'processing',  label: 'Processing'    },
  { value: 'shipped',     label: 'Shipped'       },
  { value: 'delivered',   label: 'Delivered'     },
  { value: 'cancelled',   label: 'Cancelled'     },
]

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, accent, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function StatusBadge({ status }) {
  const s = os(status)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: s.bg, color: s.color,
      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
      fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{s.label}
    </span>
  )
}

// ── Order Timeline ────────────────────────────────────────────────────────────
function OrderTimeline({ status }) {
  const current     = TIMELINE.indexOf(status?.toLowerCase())
  const isCancelled = status?.toLowerCase() === 'cancelled'
  const isReturned  = status?.toLowerCase() === 'returned'

  if (isCancelled || isReturned) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', marginBottom: 12 }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#fef2f2', border: '2px solid #ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
        {isCancelled ? '✕' : '↩'}
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>{isCancelled ? 'Order Cancelled' : 'Order Returned'}</div>
        <div style={{ fontSize: 11, color: '#9a9a94' }}>This order was {isCancelled ? 'cancelled' : 'returned'}</div>
      </div>
    </div>
  )

  return (
    <div style={{ position: 'relative', padding: '12px 0', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 14, left: 14, right: 14, height: 2, background: '#e9e9e4', zIndex: 0 }} />
        <div style={{ position: 'absolute', top: 14, left: 14, height: 2, background: '#e8440a', zIndex: 0,
          width: current >= 0 ? `${(current / (TIMELINE.length - 1)) * (100 - 28 / TIMELINE.length)}%` : '0%',
          transition: 'width 0.4s' }} />
        {TIMELINE.map((step, i) => {
          const done   = i <= current
          const active = i === current
          const s      = os(step)
          return (
            <div key={step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, zIndex: 1 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: done ? s.dot : '#fff',
                border: `2px solid ${done ? s.dot : '#e9e9e4'}`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 10, color: done ? '#fff' : '#9a9a94', fontWeight: 800,
                transition: 'all 0.3s', boxShadow: active ? `0 0 0 4px ${s.dot}22` : 'none' }}>
                {done && i < current ? '✓' : i + 1}
              </div>
              <div style={{ fontSize: 9, fontWeight: active ? 700 : 500, color: active ? s.color : '#9a9a94',
                textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'JetBrains Mono',monospace" }}>
                {step}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Delivery Chat ─────────────────────────────────────────────────────────────
const ROLE_COLOR = { seller: '#F5A623', driver: '#3B82F6', buyer: '#22C55E' }
const ROLE_LABEL = { seller: 'SELLER',  driver: 'DRIVER',  buyer: 'BUYER'  }
const QUICK_SELLER = [
  { icon: '📦', text: 'Order is packed and ready' },
  { icon: '⏱',  text: 'Give me 5 minutes'         },
]

function DeliveryChat({ order, isActive, onUnread }) {
  const { seller }           = useAuth()
  const { on, emit, connected } = useSocket()
  const [messages, setMessages] = useState([])
  const [text,     setText]     = useState('')
  const [loading,  setLoading]  = useState(false)
  const listRef    = useRef(null)
  const activeRef  = useRef(isActive)

  const orderId  = order?.order_id
  const myUserId = seller?.userId

  useEffect(() => { activeRef.current = isActive }, [isActive])
  useEffect(() => { setMessages([]); setText('') }, [orderId])

  const scrollEnd = useCallback(() => {
    setTimeout(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight
    }, 60)
  }, [])

  const addMsg = useCallback((msg) => {
    setMessages(prev => {
      if (msg.id && prev.some(m => m.id === msg.id)) return prev
      return [...prev, msg].slice(-200)
    })
    scrollEnd()
    if (!activeRef.current) onUnread?.()
  }, [scrollEnd, onUnread])

  // Join room, load history, listen for messages
  useEffect(() => {
    if (!orderId || !connected) return
    setLoading(true)
    emit('delivery:join', { orderId })

    const offJoined  = on('delivery:joined',  ()               => setLoading(false))
    const offHistory = on('delivery:history', ({ messages: h }) => {
      setLoading(false)
      if (!Array.isArray(h)) return
      setMessages(prev => {
        const seen   = new Set(prev.map(m => m.id))
        const merged = [...prev, ...h.filter(m => !seen.has(m.id))]
        merged.sort((a, b) => (a.ts ?? 0) - (b.ts ?? 0))
        return merged.slice(-200)
      })
      scrollEnd()
    })
    const offMsg = on('delivery:message', addMsg)

    return () => {
      offJoined(); offHistory(); offMsg()
      emit('delivery:leave', { orderId })
    }
  }, [orderId, connected, on, emit, addMsg, scrollEnd])

  const send = useCallback((preset) => {
    const txt = (preset ?? text).trim()
    if (!txt || !orderId) return
    const id = `${myUserId}_${Date.now()}`
    emit('delivery:message', { orderId, text: txt, id })
    addMsg({ id, orderId: String(orderId), text: txt, role: 'seller',
             userId: String(myUserId), username: seller?.username ?? 'Me', ts: Date.now() })
    setText('')
  }, [text, orderId, emit, myUserId, seller?.username, addMsg])

  const contacts = [
    { role: 'buyer',  name: order?.buyer_name  || 'Buyer'  },
    ...(order?.driver_id ? [{ role: 'driver', name: order?.driver_name || 'Driver' }] : []),
  ]
  const callTarget = order?.driver_phone
    ? { name: order.driver_name, phone: order.driver_phone }
    : order?.buyer_phone
      ? { name: order.buyer_name, phone: order.buyer_phone }
      : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Participants strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 10, borderBottom: '1px solid #f3f3f0', marginBottom: 10, flexWrap: 'wrap' }}>
        {/* You (seller) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: ROLE_COLOR.seller }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#111317' }}>You</span>
        </div>
        {contacts.map((c, i) => (
          <div key={c.role} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#d1d5db', fontSize: 11 }}>·</span>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: ROLE_COLOR[c.role] }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{c.name}</span>
            <span style={{ fontSize: 8, fontWeight: 900, color: ROLE_COLOR[c.role], background: ROLE_COLOR[c.role] + '22', border: `1px solid ${ROLE_COLOR[c.role]}44`, borderRadius: 3, padding: '1px 4px', fontFamily: "'JetBrains Mono',monospace" }}>
              {ROLE_LABEL[c.role]}
            </span>
          </div>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: connected ? '#16a34a' : '#f59e0b' }} />
          <span style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
            {connected ? 'Live' : 'Reconnecting…'}
          </span>
        </div>
      </div>

      {/* Message list */}
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 4, minHeight: 0 }}>
        {loading && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 12, padding: '24px 0' }}>Loading messages…</div>
        )}
        {!loading && messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '28px 0' }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>💬</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>No messages yet</div>
            <div style={{ fontSize: 12, color: '#9a9a94' }}>
              Coordinate with the{order?.driver_id ? ' driver and' : ''} buyer
            </div>
          </div>
        )}
        {messages.map(msg => {
          const isMe   = String(msg.userId) === String(myUserId)
          const dotClr = ROLE_COLOR[msg.role] ?? '#6b7280'
          const ts     = msg.ts ? new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''
          return (
            <div key={msg.id} style={{ display: 'flex', alignItems: 'flex-end', gap: 6,
              flexDirection: isMe ? 'row-reverse' : 'row' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotClr,
                flexShrink: 0, marginBottom: 3 }} />
              <div style={{ maxWidth: '72%', background: isMe ? '#e8440a' : '#f3f3f0',
                borderRadius: 14, borderTopRightRadius: isMe ? 4 : 14,
                borderTopLeftRadius: isMe ? 14 : 4,
                padding: '8px 11px', border: isMe ? 'none' : '1px solid #e9e9e4' }}>
                {!isMe && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: dotClr }}>
                      {msg.username || 'User'}
                    </span>
                    <span style={{ fontSize: 8, fontWeight: 900, color: dotClr,
                      background: dotClr + '22', border: `1px solid ${dotClr}44`,
                      borderRadius: 3, padding: '1px 4px',
                      fontFamily: "'JetBrains Mono',monospace" }}>
                      {ROLE_LABEL[msg.role] ?? msg.role?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div style={{ fontSize: 13, color: isMe ? '#fff' : '#111317', lineHeight: 1.5 }}>
                  {msg.text}
                </div>
                <div style={{ fontSize: 9, color: isMe ? 'rgba(255,255,255,0.65)' : '#9a9a94',
                  marginTop: 3, textAlign: 'right' }}>
                  {ts}{isMe && '  ✓'}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Quick replies + call */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 0 6px',
        borderTop: '1px solid #f3f3f0' }}>
        {QUICK_SELLER.map((r, i) => (
          <button key={i} onClick={() => send(r.text)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 16,
              border: '1px solid #e9e9e4', background: '#fff', padding: '4px 10px',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#374151' }}>
            {r.icon} {r.text}
          </button>
        ))}
        {callTarget?.phone && (
          <a href={`tel:${callTarget.phone}`}
            style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 16,
              border: '1px solid #bbf7d0', background: '#f0fdf4', padding: '4px 10px',
              fontSize: 11, fontWeight: 700, textDecoration: 'none', color: '#15803d' }}>
            📞 Call {callTarget.name?.split(' ')[0] || 'Driver'}
          </a>
        )}
      </div>

      {/* Text input */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end',
        paddingTop: 8, borderTop: '1px solid #e9e9e4' }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
          placeholder="Type a message… (Enter to send)"
          maxLength={500}
          rows={1}
          style={{ flex: 1, borderRadius: 12,
            border: `1.5px solid ${text.trim() ? '#e8440a' : '#e9e9e4'}`,
            padding: '8px 12px', fontSize: 13,
            fontFamily: "'Plus Jakarta Sans',sans-serif",
            outline: 'none', resize: 'none',
            maxHeight: 80, minHeight: 38, lineHeight: 1.5, boxSizing: 'border-box',
            transition: 'border-color 0.15s' }}
        />
        <button onClick={() => send()} disabled={!text.trim()}
          style={{ width: 40, height: 40, borderRadius: 11, border: 'none', flexShrink: 0,
            background: text.trim() ? '#e8440a' : '#f3f3f0',
            cursor: text.trim() ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: text.trim() ? '#fff' : '#9a9a94',
            transition: 'background 0.15s' }}>
          ➤
        </button>
      </div>
    </div>
  )
}

// ── Order Drawer ──────────────────────────────────────────────────────────────
function OrderDrawer({ order, onClose, onUpdated }) {
  const [tab,            setTab]            = useState('details')
  const [chatUnread,     setChatUnread]     = useState(0)
  const [updating,       setUpdating]       = useState(null)
  const [showAssign,     setShowAssign]     = useState(false)
  const [drivers,        setDrivers]        = useState([])      // from API
  const [liveDrivers,    setLiveDrivers]    = useState({})      // userId → location payload (real-time)
  const [loadingDrivers, setLoadingDrivers] = useState(false)
  const [assigning,      setAssigning]      = useState(null)    // driver_id | 'self' | null
  const [driverSearch,   setDriverSearch]   = useState('')
  const [assignError,    setAssignError]    = useState('')

  const { on, emit, clearChatUnread } = useSocket()

  useEffect(() => {
    if (order) { setTab('details'); setShowAssign(false); setAssignError(''); setChatUnread(0) }
  }, [order?.order_id])

  useEffect(() => { if (tab === 'chat') { setChatUnread(0); clearChatUnread() } }, [tab, clearChatUnread])

  // ── Watch real-time driver locations while the assign panel is open ──────
  useEffect(() => {
    if (!showAssign) return
    // Subscribe to real-time driver pings
    const unsub = on('driver:location', (payload) => {
      setLiveDrivers(prev => ({ ...prev, [String(payload.userId)]: payload }))
    })
    // Ask server to send nearby drivers (Rwanda-wide radius as fallback)
    emit('seller:watch_drivers', { shopLat: -1.9403, shopLng: 29.8739, radius: 500000 })
    return () => {
      unsub()
      emit('seller:unwatch_drivers')
    }
  }, [showAssign, on, emit])

  const openAssignPanel = async () => {
    setTab('delivery')           // switch to Delivery tab immediately
    setShowAssign(true); setAssignError(''); setDriverSearch('')
    setLiveDrivers({})
    setLoadingDrivers(true)
    try {
      const res = await api.get('/driver/available')
      setDrivers(res.data.drivers || [])
    } catch { setDrivers([]) }
    finally { setLoadingDrivers(false) }
  }

  // Merge API drivers + real-time: live drivers first (they have distance/location data)
  const mergedDrivers = (() => {
    const apiIds  = new Set(drivers.map(d => String(d.user_id)))
    const liveIds = new Set(Object.keys(liveDrivers))
    // Real-time drivers that are also in the available list
    const live = Object.values(liveDrivers)
      .filter(d => apiIds.has(String(d.userId)))
      .map(d => ({ ...d, user_id: d.userId, isLive: true }))
    // API-only drivers (not broadcasting location right now)
    const apiOnly = drivers
      .filter(d => !liveIds.has(String(d.user_id)))
      .map(d => ({ ...d, isLive: false }))
    return [...live, ...apiOnly]
  })()

  const nearest = mergedDrivers[0] ?? null
  const isSelf  = !loadingDrivers && !nearest

  // Combined: assign driver + advance to shipped (mirrors the app's handleUpdateStatus)
  const handleAssignAndShip = async () => {
    const driverId = isSelf ? null : nearest?.user_id ?? null
    setAssigning(driverId ?? 'self'); setAssignError('')
    try {
      // 1. Mark as shipped
      await api.put(`/orders/${order.order_id}/status`, { status: 'shipped' })
      // 2. Assign driver / self-delivery
      const body = driverId ? { driver_id: driverId } : { self_delivery: true }
      await api.put(`/orders/${order.order_id}/assign-driver`, body).catch(() => {})
      const selected = driverId ? mergedDrivers.find(d => String(d.user_id) === String(driverId)) : null
      onUpdated({
        ...order,
        status:              'shipped',
        driver_id:           driverId ?? null,
        is_self_delivery:    !driverId,
        delivery_status:     'assigned',
        driver_name:         selected?.name            ?? null,
        driver_phone:        selected?.phone           ?? null,
        driver_company:      selected?.vehicle_company ?? null,
        driver_vehicle_type: selected?.vehicle_type   ?? null,
        driver_plate:        selected?.plate_number   ?? null,
      })
      setShowAssign(false)
    } catch (err) {
      setAssignError(err.response?.data?.error || 'Failed. Please try again.')
    } finally { setAssigning(null) }
  }

  // Standalone assign (from Delivery tab Reassign button — doesn't change status)
  const handleAssignOnly = async (driverId) => {
    setAssigning(driverId ?? 'self'); setAssignError('')
    try {
      const body = driverId ? { driver_id: driverId } : { self_delivery: true }
      await api.put(`/orders/${order.order_id}/assign-driver`, body)
      const selected = driverId ? mergedDrivers.find(d => String(d.user_id) === String(driverId)) : null
      onUpdated({
        ...order,
        driver_id:           driverId ?? null,
        is_self_delivery:    !driverId,
        delivery_status:     'assigned',
        driver_name:         selected?.name            ?? null,
        driver_phone:        selected?.phone           ?? null,
        driver_company:      selected?.vehicle_company ?? null,
        driver_vehicle_type: selected?.vehicle_type   ?? null,
        driver_plate:        selected?.plate_number   ?? null,
      })
      setShowAssign(false)
    } catch (err) {
      setAssignError(err.response?.data?.error || 'Failed to assign. Please try again.')
    } finally { setAssigning(null) }
  }

  if (!order) return null

  const isProcessing = order.status?.toLowerCase() === 'processing'
  const nextStatuses = NEXT_STATUS[order.status?.toLowerCase()] || []
  const items        = Array.isArray(order.items) ? order.items : []
  const shopTotal    = order.shop_total ?? items.reduce((s, i) => s + Number(i.unit_price) * i.quantity, 0)
  const bg           = avatarBg(order.buyer_name || order.buyer_phone || '')

  const updateStatus = async (newStatus) => {
    // processing → shipped: always go through assign modal like the app
    if (newStatus === 'shipped') { openAssignPanel(); return }
    setUpdating(newStatus)
    try {
      await api.put(`/orders/${order.order_id}/status`, { status: newStatus })
      onUpdated({ ...order, status: newStatus })
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status')
    } finally { setUpdating(null) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 520, background: '#fff', zIndex: 201,
        display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, flexShrink: 0 }}>
            {initials(order.buyer_name || order.buyer_phone)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>Order #{order.order_id}</div>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
              {order.buyer_name || 'Anonymous'} · {fmtTime(order.created_at)}
            </div>
          </div>
          <StatusBadge status={order.status} />
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', marginLeft: 6 }}>×</div>
        </div>

        {/* Timeline */}
        <div style={{ padding: '12px 22px 0', borderBottom: '1px solid #e9e9e4' }}>
          <OrderTimeline status={order.status} />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', padding: '0 22px' }}>
          {[['details','Details'],['items','Items'],['delivery','Delivery'],['chat','Chat'],['refund','Refund']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === id ? '#111317' : '#9a9a94',
                borderBottom: `2px solid ${tab === id ? '#e8440a' : 'transparent'}`, marginBottom: -1, position: 'relative' }}>
              {label}
              {id === 'chat' && chatUnread > 0 && (
                <span style={{ position: 'absolute', top: 6, right: 4, width: 16, height: 16, borderRadius: '50%', background: '#e8440a', color: '#fff', fontSize: 8, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono',monospace" }}>
                  {chatUnread > 9 ? '9+' : chatUnread}
                </span>
              )}
              {id === 'refund' && order.refund_status && (
                <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, background: order.refund_status === 'refunded' ? '#f0fdf4' : '#fffbeb', color: order.refund_status === 'refunded' ? '#15803d' : '#b45309', padding: '1px 5px', borderRadius: 3, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>
                  {order.refund_status}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: tab === 'chat' ? 'hidden' : 'auto', padding: 22, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {/* ── Details ── */}
          {tab === 'details' && (
            <>
              {[
                ['Order ID',        `#${order.order_id}`],
                ['Buyer',           order.buyer_name   || '—'],
                ['Phone',           order.buyer_phone  || '—'],
                ['Order Status',    <StatusBadge status={order.status} />],
                ['Payment Status',  order.payment_status  || '—'],
                ['Payment Method',  order.payment_method ? order.payment_method.toUpperCase() : '—'],
                ['Shop Subtotal',   rwf(shopTotal)],
                ['Delivery Fee',    rwf(order.delivery_fee)],
                ['Total',           rwf(shopTotal + Number(order.delivery_fee || 0))],
                ['Placed',          fmtTime(order.created_at)],
                ['Updated',         fmtTime(order.updated_at)],
                ...(order.buyer_note ? [['Buyer Note', order.buyer_note]] : []),
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #f3f3f0' }}>
                  <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, fontFamily: typeof v === 'string' ? "'JetBrains Mono',monospace" : 'inherit', color: '#111317', textAlign: 'right', maxWidth: 270, wordBreak: 'break-all' }}>{v}</span>
                </div>
              ))}
            </>
          )}

          {/* ── Items ── */}
          {tab === 'items' && (
            !items.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No items found.</div>
              : <>
                  {items.map((item, i) => {
                    const imgUrl = parseImage(item.images)
                    return (
                      <div key={item.prod_id || i} style={{ padding: '12px 0', borderBottom: '1px solid #f3f3f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 44, height: 44, borderRadius: 8, background: '#f3f3f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0, overflow: 'hidden' }}>
                            {imgUrl
                              ? <img src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                              : '📦'}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                            {item.variant_label && (
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', borderRadius: 4, padding: '1px 6px', display: 'inline-flex', marginTop: 2, fontFamily: "'JetBrains Mono',monospace" }}>
                                {item.variant_label}
                              </div>
                            )}
                            <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>×{item.quantity}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace" }}>{rwf(item.unit_price * item.quantity)}</div>
                            <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{rwf(item.unit_price)} each</div>
                          </div>
                        </div>

                        {/* Delivery fee per item */}
                        {Number(item.delivery_fee) > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, padding: '5px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>Delivery</span>
                            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>{rwf(item.delivery_fee)}</span>
                          </div>
                        )}
                      </div>
                    )
                  })}

                  {/* Order total */}
                  <div style={{ marginTop: 12, background: '#f9f9f7', borderRadius: 8, padding: '12px 14px' }}>
                    {[
                      ['Shop Subtotal', rwf(shopTotal)],
                      ['Delivery Fee',  rwf(order.delivery_fee)],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #efefed' }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{k}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace" }}>{v}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0' }}>
                      <span style={{ fontSize: 13, fontWeight: 800 }}>Total</span>
                      <span style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>
                        {rwf(shopTotal + Number(order.delivery_fee || 0))}
                      </span>
                    </div>
                  </div>
                </>
          )}

          {/* ── Delivery ── */}
          {tab === 'delivery' && (
            <div>
              {/* Address */}
              <div style={{ background: '#f9f9f7', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '1px solid #e9e9e4' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>Delivery Address</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111317', lineHeight: 1.6 }}>
                  {order.delivery_address || 'No delivery address set'}
                </div>
              </div>

              {/* Self-delivery */}
              {order.is_self_delivery && (
                <div style={{ background: '#fff3f0', border: '1px solid #fdd0c0', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#e8440a', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace" }}>🛵 Self Delivery</div>
                    {!['cancelled','returned','delivered'].includes(order.status?.toLowerCase()) && (
                      <button onClick={openAssignPanel} style={{ fontSize: 10, fontWeight: 700, color: '#e8440a', background: 'rgba(232,68,10,0.1)', border: '1px solid rgba(232,68,10,0.25)', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                        ↺ Reassign
                      </button>
                    )}
                  </div>
                  <div style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#92400e', lineHeight: 1.6, border: '1px solid #fdd0c0' }}>
                    You are handling delivery for this order. Contact the buyer directly if needed.
                  </div>
                  {[
                    ['Delivery Status', order.delivery_status || 'self_delivery'],
                    ['Delivery Fee',    rwf(order.delivery_fee)],
                    ['Delivered At',    order.delivered_at ? fmtTime(order.delivered_at) : '—'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(0,0,0,0.06)', marginTop: 2 }}>
                      <span style={{ fontSize: 11, color: '#e8440a', fontWeight: 600 }}>{k}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Third-party driver */}
              {!order.is_self_delivery && order.driver_id && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace" }}>🚗 Assigned Driver</div>
                    {!['cancelled','returned','delivered'].includes(order.status?.toLowerCase()) && (
                      <button onClick={openAssignPanel} style={{ fontSize: 10, fontWeight: 700, color: '#15803d', background: 'rgba(21,128,61,0.1)', border: '1px solid rgba(21,128,61,0.25)', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                        ↺ Reassign
                      </button>
                    )}
                  </div>

                  {/* Driver identity */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: avatarBg(order.driver_name || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, flexShrink: 0 }}>
                      {initials(order.driver_name || order.driver_phone)}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{order.driver_name || 'Driver'}</div>
                      {order.driver_phone && (
                        <div style={{ fontSize: 11, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>{order.driver_phone}</div>
                      )}
                    </div>
                  </div>

                  {[
                    order.driver_company    && ['Company',    order.driver_company],
                    order.driver_vehicle_type && ['Vehicle',  order.driver_vehicle_type],
                    order.driver_plate      && ['Plate',      order.driver_plate],
                    ['Delivery Status',       order.delivery_status || '—'],
                    ['Delivered At',          order.delivered_at ? fmtTime(order.delivered_at) : '—'],
                  ].filter(Boolean).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                      <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>{k}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}

              {!order.is_self_delivery && !order.driver_id && (
                <div style={{ background: '#fafaf8', border: '1px solid #e9e9e4', borderRadius: 8, padding: '14px', fontSize: 12, color: '#9a9a94', textAlign: 'center', marginBottom: 16 }}>
                  No driver assigned to this order yet.
                </div>
              )}

              {/* ── Assign / Reassign button ── */}
              {!['cancelled','returned','delivered'].includes(order.status?.toLowerCase()) && !showAssign && (
                <button onClick={openAssignPanel}
                  style={{ width: '100%', height: 40, borderRadius: 8, border: '1.5px dashed #e9e9e4', background: '#fafaf8', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: 16 }}>
                  {order.driver_id || order.is_self_delivery ? '↺ Reassign Delivery' : '+ Assign Delivery'}
                </button>
              )}

              {/* ── Assign panel ── */}
              {showAssign && (
                <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800 }}>Delivery Assignment</div>
                      <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 2 }}>Order #{order.order_id} · packed & ready for pickup</div>
                    </div>
                    <button onClick={() => { setShowAssign(false); setAssignError('') }}
                      style={{ fontSize: 18, color: '#9a9a94', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}>×</button>
                  </div>

                  {/* Delivery address */}
                  {order.delivery_address && (
                    <div style={{ padding: '8px 16px', borderBottom: '1px solid #f3f3f0', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ fontSize: 13, color: '#9a9a94', marginTop: 1 }}>📍</span>
                      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{order.delivery_address}</div>
                    </div>
                  )}

                  {/* Auto-selected nearest driver (or self-delivery fallback) */}
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f3f0' }}>
                    {loadingDrivers ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                        <div style={{ width: 46, height: 46, borderRadius: 14, background: '#f3f3f0', flexShrink: 0 }} />
                        <div style={{ fontSize: 13, color: '#9a9a94' }}>Finding nearest driver…</div>
                      </div>
                    ) : nearest ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'rgba(232,68,10,0.05)', border: '1.5px solid rgba(232,68,10,0.2)', borderRadius: 12 }}>
                        <div style={{ width: 46, height: 46, borderRadius: 14, background: '#e8440a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
                          {initials(nearest.name || nearest.phone)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <div style={{ fontSize: 14, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {nearest.name || 'Driver'}
                            </div>
                            {nearest.isLive && (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(232,68,10,0.1)', border: '1px solid rgba(232,68,10,0.35)', borderRadius: 5, padding: '1px 6px' }}>
                                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#e8440a' }} />
                                <span style={{ fontSize: 8, fontWeight: 900, color: '#e8440a', fontFamily: "'JetBrains Mono',monospace" }}>LIVE</span>
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            {nearest.phone && <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{nearest.phone}</span>}
                            {nearest.vehicle_type && <span style={{ fontSize: 11, color: '#6b7280' }}>🚗 {nearest.vehicle_type}</span>}
                            {(nearest.driver_rating ?? nearest.rating) && (
                              <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700 }}>★ {Number(nearest.driver_rating ?? nearest.rating).toFixed(1)}</span>
                            )}
                            {nearest.distance_m != null && (
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#e8440a' }}>
                                📍 {nearest.distance_m >= 1000 ? `${(nearest.distance_m / 1000).toFixed(1)} km` : `${Math.round(nearest.distance_m)} m`} away
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ background: '#e8440a', color: '#fff', fontSize: 8, fontWeight: 900, padding: '3px 7px', borderRadius: 7, fontFamily: "'JetBrains Mono',monospace", flexShrink: 0 }}>
                          NEAREST
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 12 }}>
                        <div style={{ width: 46, height: 46, borderRadius: 14, background: '#e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🏪</div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 800 }}>No drivers available</div>
                          <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2 }}>You'll handle this delivery yourself</div>
                        </div>
                        <span style={{ marginLeft: 'auto', fontSize: 18 }}>⚠️</span>
                      </div>
                    )}
                  </div>

                  {/* Other available drivers */}
                  {!loadingDrivers && mergedDrivers.length > 1 && (
                    <>
                      <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", borderBottom: '1px solid #f3f3f0' }}>
                        Other drivers
                      </div>
                      {/* Search */}
                      <div style={{ padding: '8px 16px', borderBottom: '1px solid #f3f3f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '6px 10px' }}>
                          <span style={{ fontSize: 13, color: '#9a9a94' }}>🔍</span>
                          <input value={driverSearch} onChange={e => setDriverSearch(e.target.value)} placeholder="Search drivers…"
                            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
                        </div>
                      </div>
                      <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                        {mergedDrivers.slice(1).filter(d =>
                          !driverSearch ||
                          (d.name  || '').toLowerCase().includes(driverSearch.toLowerCase()) ||
                          (d.phone || '').includes(driverSearch)
                        ).map(d => (
                          <div key={d.user_id}
                            onClick={() => !assigning && handleAssignOnly(d.user_id)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderBottom: '1px solid #f3f3f0', cursor: assigning ? 'not-allowed' : 'pointer', transition: 'background 0.1s' }}
                            onMouseEnter={e => { if (!assigning) e.currentTarget.style.background = '#f9f9f7' }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: avatarBg(d.name || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                              {initials(d.name || d.phone)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || 'Driver'}</div>
                                {d.isLive && (
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: 'rgba(232,68,10,0.08)', border: '1px solid rgba(232,68,10,0.25)', borderRadius: 4, padding: '1px 5px' }}>
                                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#e8440a' }} />
                                    <span style={{ fontSize: 7, fontWeight: 900, color: '#e8440a', fontFamily: "'JetBrains Mono',monospace" }}>LIVE</span>
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                                {[d.phone, d.vehicle_type, d.plate_number].filter(Boolean).join(' · ')}
                              </div>
                            </div>
                            {(d.driver_rating ?? d.rating) && (
                              <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>★ {Number(d.driver_rating ?? d.rating).toFixed(1)}</span>
                            )}
                            {assigning === d.user_id && <span style={{ fontSize: 11, color: '#15803d', fontWeight: 700 }}>Assigning…</span>}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {assignError && (
                    <div style={{ padding: '10px 16px', background: '#fef2f2', borderTop: '1px solid #fecaca', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ {assignError}</div>
                  )}

                  {/* Confirm footer */}
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 8 }}>
                    <button onClick={() => { setShowAssign(false); setAssignError('') }}
                      style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#6b7280' }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => isProcessing ? handleAssignAndShip() : handleAssignOnly(nearest?.user_id ?? null)}
                      disabled={!!assigning || loadingDrivers}
                      style={{ flex: 2, height: 42, borderRadius: 8, border: 'none',
                        background: assigning || loadingDrivers ? '#f0c4b4' : '#e8440a',
                        color: '#fff', fontSize: 13, fontWeight: 700,
                        cursor: assigning || loadingDrivers ? 'not-allowed' : 'pointer',
                        fontFamily: "'Plus Jakarta Sans',sans-serif",
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      {assigning
                        ? 'Assigning…'
                        : isProcessing
                          ? (isSelf ? '🛵 Handle Delivery' : '🚗 Assign & Ship')
                          : (isSelf ? '🛵 Self Delivery' : '✓ Assign Driver')}
                    </button>
                  </div>
                </div>
              )}

              {/* Buyer note */}
              {order.buyer_note && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 14px', marginTop: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>Buyer Note</div>
                  <div style={{ fontSize: 13, color: '#0c4a6e', lineHeight: 1.6 }}>{order.buyer_note}</div>
                </div>
              )}

              {/* Driver note */}
              {order.driver_note && (
                <div style={{ background: order.delivery_status === 'failed' ? '#fef2f2' : '#fffbeb', border: `1px solid ${order.delivery_status === 'failed' ? '#fecaca' : '#fde68a'}`, borderRadius: 8, padding: '12px 14px', marginTop: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: order.delivery_status === 'failed' ? '#b91c1c' : '#92400e', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>
                    {order.delivery_status === 'failed' ? '⚠ Driver Note (Failure)' : 'Driver Note'}
                  </div>
                  <div style={{ fontSize: 13, color: order.delivery_status === 'failed' ? '#991b1b' : '#78350f', lineHeight: 1.6 }}>{order.driver_note}</div>
                </div>
              )}
            </div>
          )}

          {/* ── Chat ── */}
          {/* Always mounted so socket stays connected; hidden when not active */}
          <div style={{ display: tab === 'chat' ? 'flex' : 'none', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <DeliveryChat
              order={order}
              isActive={tab === 'chat'}
              onUnread={() => setChatUnread(c => c + 1)}
            />
          </div>

          {/* ── Refund ── */}
          {tab === 'refund' && (
            <div>
              {order.refund_status ? (() => {
                const RS = {
                  pending:    { bg: '#fffbeb', color: '#b45309', label: 'Pending Review' },
                  processing: { bg: '#eff6ff', color: '#1d4ed8', label: 'Processing'     },
                  approved:   { bg: '#f0fdf4', color: '#15803d', label: 'Approved'       },
                  refunded:   { bg: '#f0fdf4', color: '#15803d', label: 'Refunded ✓'     },
                  rejected:   { bg: '#fef2f2', color: '#b91c1c', label: 'Rejected'       },
                  cancelled:  { bg: '#f3f4f6', color: '#6b7280', label: 'Cancelled'      },
                }
                const rs = RS[order.refund_status] || RS.pending
                return (
                  <div>
                    <div style={{ background: rs.bg, border: `1px solid ${rs.color}33`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: rs.color, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace" }}>Refund Status</div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: rs.color, marginTop: 4 }}>{rs.label}</div>
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.7, background: '#f9f9f7', borderRadius: 8, padding: '12px 14px', border: '1px solid #e9e9e4' }}>
                      {order.refund_status === 'pending'
                        ? 'A refund request has been submitted for this order and is under review by our team.'
                        : order.refund_status === 'approved' || order.refund_status === 'processing'
                          ? 'This refund has been approved. The buyer will receive their money shortly.'
                          : order.refund_status === 'refunded'
                            ? 'The buyer has been fully refunded for this order.'
                            : order.refund_status === 'rejected'
                              ? 'The refund request was reviewed and rejected.'
                              : 'This refund has been cancelled.'}
                    </div>
                  </div>
                )
              })() : (
                <div style={{ background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 10, padding: '40px', textAlign: 'center' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>No Refund</div>
                  <div style={{ fontSize: 12, color: '#9a9a94' }}>No refund has been requested for this order.</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — status actions */}
        {(() => {
          // A third-party driver is handling this delivery — seller cannot mark delivered
          // unless the driver reported a failure (delivery_status === 'failed')
          const isTerminal          = ['delivered','cancelled','returned'].includes(order.status?.toLowerCase())
          if (isTerminal) return null   // order is done — hide all action buttons

          const hasThirdPartyDriver = order.driver_id && !order.is_self_delivery
          const driverFailed        = order.delivery_status === 'failed'
          const blockDelivered      = hasThirdPartyDriver && !driverFailed

          const advanceStatuses = nextStatuses.filter(s => s !== 'cancelled')
          const blockedStatuses = blockDelivered
            ? advanceStatuses.filter(s => s === 'delivered')
            : []
          const allowedStatuses = advanceStatuses.filter(s => !blockedStatuses.includes(s))

          return (
            <div style={{ padding: '14px 22px', borderTop: '1px solid #e9e9e4' }}>
              {/* ── Driver is handling delivery (waiting) ── */}
              {blockDelivered && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>🚗</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1' }}>
                      {order.driver_name ? `${order.driver_name} is handling delivery` : 'Driver is handling delivery'}
                    </div>
                    <div style={{ fontSize: 11, color: '#7dd3fc', marginTop: 2 }}>
                      Delivery will be marked automatically when the driver confirms.
                    </div>
                  </div>
                </div>
              )}

              {/* ── Driver reported a failure — three resolution actions ── */}
              {hasThirdPartyDriver && driverFailed && (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>Driver reported a failure</div>
                      {order.driver_note && (
                        <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', margin: '3px 0' }}>
                          "{order.driver_note}"
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 2 }}>Choose how to resolve this order.</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    {/* Reassign Driver */}
                    <button onClick={openAssignPanel} disabled={!!updating}
                      style={{ flex: 1, height: 40, borderRadius: 9, border: '1px solid #bae6fd', background: '#f0f9ff',
                        color: '#0369a1', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                      🚗 Reassign Driver
                    </button>
                    {/* Mark Delivered */}
                    <button onClick={() => updateStatus('delivered')} disabled={!!updating}
                      style={{ flex: 1, height: 40, borderRadius: 9, border: '1px solid #bbf7d0', background: '#f0fdf4',
                        color: '#15803d', fontSize: 12, fontWeight: 700, cursor: updating ? 'not-allowed' : 'pointer',
                        fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                      {updating === 'delivered' ? 'Marking…' : '✓ Mark Delivered'}
                    </button>
                    {/* Cancel & Refund */}
                    <button onClick={() => updateStatus('cancelled')} disabled={!!updating}
                      style={{ flex: 1, height: 40, borderRadius: 9, border: '1px solid #fecaca', background: '#fef2f2',
                        color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: updating ? 'not-allowed' : 'pointer',
                        fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                      {updating === 'cancelled' ? 'Cancelling…' : '✕ Cancel & Refund'}
                    </button>
                  </div>
                </>
              )}

              {/* ── Normal advance buttons (non-failed path) ── */}
              {allowedStatuses.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: nextStatuses.includes('cancelled') && !hasThirdPartyDriver ? 8 : 0 }}>
                  {allowedStatuses.map(s => (
                    <button key={s} onClick={() => updateStatus(s)} disabled={!!updating}
                      style={{ flex: 1, height: 42, borderRadius: 8, border: 'none',
                        background: updating === s ? '#f0c4b4' : '#e8440a', color: '#fff',
                        fontSize: 13, fontWeight: 700, cursor: updating ? 'not-allowed' : 'pointer',
                        fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                      {updating === s ? 'Updating…'
                        : s === 'shipped' ? '🚗 Ready — Assign Driver →'
                        : `Mark as ${s.charAt(0).toUpperCase() + s.slice(1)} →`}
                    </button>
                  ))}
                </div>
              )}

              {/* Cancel button — hide when failure resolution row already shows it */}
              {nextStatuses.includes('cancelled') && !(hasThirdPartyDriver && driverFailed) && (
                <button onClick={() => updateStatus('cancelled')} disabled={!!updating}
                  style={{ width: '100%', height: 38, borderRadius: 8, border: '1px solid #fecaca',
                    background: '#fff5f5', color: '#b91c1c', fontSize: 12, fontWeight: 700,
                    cursor: updating ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {updating === 'cancelled' ? 'Cancelling…' : 'Cancel Order'}
                </button>
              )}
            </div>
          )
        })()}
      </div>
    </>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4',
        background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317',
        fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
        padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Orders() {
  const [orders,       setOrders]       = useState([])
  const [loading,      setLoading]      = useState(false)
  const now        = new Date()
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`
  const monthEnd   = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().slice(0,10)

  const [statusFilter, setStatusFilter] = useState('')
  const [searchInput,  setSearchInput]  = useState('')
  const [search,       setSearch]       = useState('')
  const [dateFrom,     setDateFrom]     = useState(monthStart)
  const [dateTo,       setDateTo]       = useState(monthEnd)
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [total,        setTotal]        = useState(0)
  const [stats,        setStats]        = useState({})
  const [selected,     setSelected]     = useState(null)
  const [liveToast,    setLiveToast]    = useState(null) // { message, type }
  const fetchRef   = useRef(0)
  const toastTimer = useRef(null)
  const PER_PAGE   = 15

  const { on, setPendingCount, clearChatUnread } = useSocket()

  useEffect(() => { const t = setTimeout(() => setSearch(searchInput), 400); return () => clearTimeout(t) }, [searchInput])
  useEffect(() => { setPage(1) }, [search, statusFilter, dateFrom, dateTo])

  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    const id = ++fetchRef.current
    if (!silent) setLoading(true)
    try {
      const params = new URLSearchParams({ page, limit: PER_PAGE, date_from: dateFrom, date_to: dateTo, ...(statusFilter && { status: statusFilter }), ...(search && { search }) })
      const res = await api.get(`/orders/seller/incoming?${params}`)
      if (id !== fetchRef.current) return
      const data = res.data
      setOrders(data.orders || [])
      setTotalPages(data.totalPages || 1)
      setTotal(data.total || 0)
      if (data.stats) {
        setStats(data.stats)
        setPendingCount(data.stats.pending ?? 0)
      }
    } catch {
      if (id !== fetchRef.current) return
      if (!silent) setOrders([])
    } finally { if (id === fetchRef.current && !silent) setLoading(false) }
  }, [page, search, statusFilter, dateFrom, dateTo])

  useEffect(() => { fetchOrders({}) }, [fetchOrders])

  const showLiveToast = (message, type) => {
    clearTimeout(toastTimer.current)
    setLiveToast({ message, type })
    toastTimer.current = setTimeout(() => setLiveToast(null), 4000)
  }

  // Instantly add a new order to the top of the list + update counts
  const prependOrder = (partial) => {
    setOrders(prev => prev.find(o => o.order_id === partial.order_id) ? prev : [partial, ...prev])
    setTotal(t => t + 1)
    setStats(s => ({ ...s, [partial.status]: (s[partial.status] ?? 0) + 1, all: (s.all ?? 0) + 1 }))
    setPendingCount(c => c + 1)
  }

  // ── Real-time socket updates ──────────────────────────────────────────────
  useEffect(() => {
    // Standard order placed (cart checkout) — notification goes to seller's user room
    const unsubNotif = on('notification', (notif) => {
      if (notif.type !== 'new_order') return
      const { orderId, buyerName, total } = notif.data ?? {}
      if (orderId) {
        prependOrder({
          order_id:       orderId,
          buyer_name:     buyerName  || 'Customer',
          buyer_phone:    null,
          total_amount:   total      || 0,
          shop_subtotal:  total      || 0,
          status:         'pending',
          payment_status: 'pending',
          delivery_fee:   0,
          created_at:     new Date().toISOString(),
          items:          [],
          _partial:       true,
        })
      }
      showLiveToast(
        `🛒 New order${buyerName ? ` from ${buyerName}` : ''}${total ? ` — RWF ${Number(total).toLocaleString()}` : ''}`,
        'order'
      )
      fetchOrders({ silent: true }) // background sync for complete fields
    })

    // Quick-live order placed — goes to seller's shop room
    const unsubQuick = on('quicklive:new_order', (payload) => {
      prependOrder({
        order_id:       payload.order_id,
        buyer_name:     payload.buyer_name || 'Customer',
        buyer_phone:    null,
        total_amount:   payload.total      || 0,
        shop_subtotal:  payload.total      || 0,
        status:         'pending_confirmation',
        payment_status: 'pending',
        delivery_fee:   0,
        created_at:     payload.placed_at  || new Date().toISOString(),
        items:          payload.product_name
          ? [{ name: payload.product_name, quantity: payload.quantity, unit_price: payload.total }]
          : [],
        _partial:       true,
      })
      showLiveToast(
        `⚡ Quick order${payload.buyer_name ? ` from ${payload.buyer_name}` : ''} — ${payload.product_name || 'product'} ×${payload.quantity ?? 1}`,
        'order'
      )
      fetchOrders({ silent: true })
    })

    // Order status changed by admin / driver / buyer → patch row + stats in-place
    const unsubUpdate = on('order:update', (payload) => {
      if (!payload?.order_id) return
      handleUpdated(payload)
      if (payload.status) {
        showLiveToast(`📦 Order #${payload.order_id} → ${payload.status}`, 'update')
      }
    })

    return () => { unsubNotif(); unsubQuick(); unsubUpdate() }
  }, [on, fetchOrders])

  const handleUpdated = (updated) => {
    setOrders(prev => {
      const old = prev.find(o => o.order_id === updated.order_id)
      // Patch tab-count stats immediately if the status changed
      if (updated.status && old?.status && old.status !== updated.status) {
        setStats(s => {
          const next = { ...s }
          if ((next[old.status] ?? 0) > 0) next[old.status]--
          next[updated.status] = (next[updated.status] ?? 0) + 1
          next.all = Object.entries(next)
            .filter(([k]) => k !== 'all')
            .reduce((a, [, v]) => a + (v || 0), 0)
          return next
        })
      }
      return prev.map(o => o.order_id === updated.order_id ? { ...o, ...updated } : o)
    })
    setSelected(prev => prev?.order_id === updated.order_id ? { ...prev, ...updated } : prev)
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Orders</div>
        <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Manage incoming orders and update delivery status.</div>
      </div>

      {/* Live update banner */}
      {liveToast && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: liveToast.type === 'order' ? '#fff3f0' : '#f0fdf4',
          border: `1px solid ${liveToast.type === 'order' ? '#fdd0c0' : '#bbf7d0'}`, borderRadius: 9,
          padding: '10px 16px', marginBottom: 16, fontSize: 13, fontWeight: 600,
          color: liveToast.type === 'order' ? '#c2410c' : '#15803d', animation: 'fadeIn 0.2s ease' }}>
          <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
          {liveToast.message}
          <button onClick={() => setLiveToast(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="Total"      value={(stats.all       ?? 0).toLocaleString()}                     />
        <StatCard label="Pending"    value={(stats.pending   ?? 0).toLocaleString()} accent="#b45309"    />
        <StatCard label="Processing" value={((stats.confirmed ?? 0) + (stats.processing ?? 0)).toLocaleString()} accent="#7c3aed" />
        <StatCard label="Shipped"    value={(stats.shipped   ?? 0).toLocaleString()} accent="#0369a1"    />
        <StatCard label="Delivered"  value={(stats.delivered ?? 0).toLocaleString()} accent="#15803d"    />
        <StatCard label="Cancelled"  value={(stats.cancelled ?? 0).toLocaleString()} accent="#b91c1c"    />
      </div>

      {/* Status filter tabs */}
      <div style={{ display: 'flex', background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
        {FILTERS.map(f => {
          const count  = f.value === '' ? (stats.all ?? 0) : (stats[f.value] ?? 0)
          const active = statusFilter === f.value
          return (
            <button key={f.value} onClick={() => setStatusFilter(f.value)}
              style={{ flex: 1, padding: '10px 4px', border: 'none', background: active ? '#111317' : 'transparent',
                color: active ? '#fff' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.12s',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span>{f.label}</span>
              {count > 0 && (
                <span style={{ fontSize: 10, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace",
                  background: active ? 'rgba(255,255,255,0.2)' : '#e9e9e4',
                  color: active ? '#fff' : '#374151', padding: '1px 6px', borderRadius: 3, lineHeight: 1.4 }}>
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Date range filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>
        {[0,1,2].map(n => {
          const d    = new Date(now.getFullYear(), now.getMonth() - n, 1)
          const from = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`
          const to   = new Date(d.getFullYear(), d.getMonth()+1, 0).toISOString().slice(0,10)
          const lbl  = n === 0 ? 'This month' : d.toLocaleString('en-GB', { month: 'short', year: 'numeric' })
          const active = dateFrom === from && dateTo === to
          return (
            <button key={n} onClick={() => { setDateFrom(from); setDateTo(to) }}
              style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${active ? '#111317' : '#e9e9e4'}`, background: active ? '#111317' : '#fff', color: active ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {lbl}
            </button>
          )
        })}
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>
          {fmt(dateFrom)} – {fmt(dateTo)}
        </div>
      </div>

      {/* Search toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            placeholder="Search by buyer name, phone or order ID…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
        <button onClick={fetchOrders} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{total.toLocaleString()} orders</div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>

        {/* Table header */}
        <div style={{ display: 'grid', gridTemplateColumns: '0.7fr 1.4fr 1fr 1fr 1fr 0.8fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
          {['Order', 'Buyer', 'Status', 'Amount', 'Date', 'Action'].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading orders…</div>
        ) : orders.length === 0 ? (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#374151', marginBottom: 6 }}>No orders found</div>
            <div style={{ fontSize: 13, color: '#9a9a94' }}>
              {statusFilter ? `No ${statusFilter} orders in this period.` : 'No orders for the selected period.'}
            </div>
          </div>
        ) : (
          <>
            {orders.map((order, i) => {
              const s         = os(order.status)
              const bg        = avatarBg(order.buyer_name || order.buyer_phone || '')
              const isPartial = order._partial
              return (
                <div key={order.order_id}
                  onClick={() => setSelected(order)}
                  style={{ display: 'grid', gridTemplateColumns: '0.7fr 1.4fr 1fr 1fr 1fr 0.8fr', padding: '13px 18px', borderBottom: i < orders.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', alignItems: 'center', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                  {/* Order ID */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>#{order.order_id}</div>
                    {isPartial && <div style={{ fontSize: 9, color: '#f59e0b', fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>LIVE</div>}
                  </div>

                  {/* Buyer */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                      {initials(order.buyer_name || order.buyer_phone)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.buyer_name || 'Anonymous'}</div>
                      {order.buyer_phone && <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{order.buyer_phone}</div>}
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <StatusBadge status={order.status} />
                    {order.delivery_status && order.delivery_status !== 'pending' && (
                      <div style={{ fontSize: 9, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace", marginTop: 3, textTransform: 'uppercase' }}>{order.delivery_status}</div>
                    )}
                  </div>

                  {/* Amount */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>
                      {rwf(Number(order.shop_subtotal ?? order.total_amount ?? 0))}
                    </div>
                    {Number(order.delivery_fee) > 0 && (
                      <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>+{rwf(order.delivery_fee)} delivery</div>
                    )}
                  </div>

                  {/* Date */}
                  <div>
                    <div style={{ fontSize: 11, color: '#374151', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(order.created_at)}</div>
                    <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                      {order.created_at ? new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </div>

                  {/* Action */}
                  <div>
                    <button onClick={e => { e.stopPropagation(); setSelected(order) }}
                      style={{ height: 30, padding: '0 12px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#374151' }}>
                      View →
                    </button>
                  </div>
                </div>
              )
            })}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
            Page {page} of {totalPages} · {total.toLocaleString()} orders
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Order drawer */}
      {selected && (
        <OrderDrawer
          order={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  )
}
